import { proxy, snapshot, useSnapshot } from "valtio";
import { toast } from "sonner";
import { get, put } from "@/lib/http";
import { PasswordUpdateFormValues, User } from "@/types/user.type";
import { ProfileUpdate } from "@/types/settings.type";
import { getAccountProxy } from "./account.store";

const accountProxy = getAccountProxy();

export const settingsProxy = proxy({
  isUpdatingProfile: false,
  isUpdatingPassword: false,
  isLoadingUser: false,
  userData: null as User | null,

  get userId(): string {
    return accountProxy.userInfo?.profile?.sub as string;
  },

  fetchUserData: async () => {
    settingsProxy.isLoadingUser = true;
    const userId = settingsProxy.userId;

    if (!userId) {
      toast.error("User ID not found");
      settingsProxy.isLoadingUser = false;
      return null;
    }

    try {
      const userData = await get<User>(`/api/user/${userId}/me`);
      settingsProxy.userData = userData;
      return userData;
    } catch (error) {
      toast.error("Failed to fetch user data");
      return null;
    } finally {
      settingsProxy.isLoadingUser = false;
    }
  },

  updateProfile: async (data: ProfileUpdate) => {
    const userId = settingsProxy.userId;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    settingsProxy.isUpdatingProfile = true;
    try {
      await put(`/api/user/${userId}`, {
        firstName: data.firstName,
        familyName: data.familyName,
        username: data.username,
        email: data.email
      });

      // Fetch fresh user data from the backend
      await settingsProxy.fetchUserData();
      toast.success("Profile updated successfully");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      settingsProxy.isUpdatingProfile = false;
    }
  },

  updatePassword: async (data: PasswordUpdateFormValues) => {
    const userId = settingsProxy.userId;
    if (!userId) {
      toast.error("User ID not found");
      return false;
    }

    settingsProxy.isUpdatingPassword = true;
    try {
      await put(`/api/user/${userId}/password`, {
        currentPassword: data.currentPassword,
        newPassword: data.password
      });

      toast.success("Password updated successfully");
      return true;
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
      return false;
    } finally {
      settingsProxy.isUpdatingPassword = false;
    }
  }
});

export const getSettingsSnapshot = () => snapshot(settingsProxy);
export const getSettingsProxy = () => settingsProxy;

export const useSettingsSnapshot = (
  options?: Parameters<typeof useSnapshot>[1]
) => useSnapshot(settingsProxy, options);

export const useSettingsProxy = () => settingsProxy;

export const useSettingsStore = (
  options?: Parameters<typeof useSnapshot>[1]
) => {
  return {
    settingsSnapshot: useSnapshot(settingsProxy, options),
    settingsProxy: settingsProxy
  };
};
